import ForumIcon from "@mui/icons-material/Forum";
import { styled } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { useLazyGetForumsQuery } from "messaging/_api/messaging-api";
import ForumQuery, { ForumFilterParameters, ForumOrderParameters } from "messaging/forums/_api/queries/forum-query";
import ForumChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import CreateForumDialog from "./forum-creation-dialog";
import ForumSelection from "./forum-selector";

const ForumContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "fillAvailableSpace",
})<{ fillAvailableSpace: boolean }>(({ theme, fillAvailableSpace }) => ({
  boxSizing: "border-box",
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  height: fillAvailableSpace ? "100%" : undefined,
  minHeight: fillAvailableSpace ? 0 : undefined,
  overflow: fillAvailableSpace ? "hidden" : undefined,
  paddingTop: theme.spacing(1),
  width: "100%",
}));
const SelectionContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridRowStart: 1,
}));
const SelectionActions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1),
}));
const ChatContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "fillAvailableSpace",
})<{ fillAvailableSpace: boolean }>(({ fillAvailableSpace }) => ({
  gridRowStart: 2,
  gridRowEnd: "none",
  height: fillAvailableSpace ? "100%" : undefined,
  minHeight: fillAvailableSpace ? 0 : undefined,
  overflow: fillAvailableSpace ? "hidden" : undefined,
}));
const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  padding: theme.spacing(4),
  paddingTop: 0,
  width: "100%",
}));
const UnavailableContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
  padding: theme.spacing(4),
  textAlign: "center",
  width: "100%",
}));

const forumUnavailableMessage = "This conversation is no longer available.";
const deletedItemsMessage = "This thread contains deleted items";

/**
 * Lives outside the component on purpose. Unsubscribing has to run whether or not the request
 * throws, which needs a `finally`, and the React Compiler cannot lower one — leaving this inline
 * made it skip memoizing the whole dialog. Module-level functions are not compiled, so the
 * original control flow is preserved exactly.
 */
async function loadChatDialog(
  abortController: AbortController,
  forumId: Guid | undefined,
  getForums: ReturnType<typeof useLazyGetForumsQuery>[0],
  setIsLoading: (isLoading: boolean) => void,
  setSelectedForum: (forum: Forum | undefined) => void,
  setAllForums: (forums: Forum[]) => void,
  setIsForumUnavailable: (isUnavailable: boolean) => void,
  showDeletedItemsNotice: (forumId: Guid) => void,
): Promise<void> {
  try {
    setIsLoading(true);
    setIsForumUnavailable(false);

    const filterParameters = new ForumFilterParameters({ forumId });
    const orderParameters = new ForumOrderParameters();
    const request = new ForumQuery({
      filterParameters,
      orderParameters,
    });
    // A superseded load is discarded by the `aborted` checks below rather than cancelled, because
    // the lazy trigger hands back a handle on a cache entry shared by everyone asking for this
    // query. Aborting it writes the abort error into that shared entry, and the load superseding
    // this one re-initiates the same entry in the same tick — the communications page re-opens this
    // dialog under a new id whenever the session object is replaced, so React remounts it while the
    // first request is still in flight. RTK Query then sees that request still pending, skips the
    // second thunk, and settles it from the entry the abort has just failed. The dialog reports
    // "Failed to get contacts" for a request nothing cancelled, and no later load re-requests it.
    const forumsRequest = getForums({ query: request });
    let forumsResponse;
    try {
      forumsResponse = await forumsRequest.unwrap();
    } finally {
      forumsRequest.unsubscribe();
    }
    if (abortController.signal.aborted) return;

    const forums = Forum.fromJSONArray(forumsResponse);
    if (forumId) {
      const requestedForum = forums?.[0];
      if (!requestedForum?.id?.isEqualTo(forumId)) {
        setSelectedForum(undefined);
        setIsForumUnavailable(true);
        showDeletedItemsNotice(forumId);
        return;
      }
      setSelectedForum(requestedForum);
      return;
    }
    if (!forums) {
      return;
    }
    setAllForums(forums);
  } catch (err) {
    if (abortController.signal.aborted) return;
    console.error(err);

    enqueueSnackbar("Failed to get contacts", { variant: "error" });
  } finally {
    // The load that superseded this one owns the flag; clearing it here would unhide the dialog
    // body while that load is still running.
    if (!abortController.signal.aborted) {
      setIsLoading(false);
    }
  }
}

type ChatDialogProps = {
  fillAvailableSpace?: boolean;
  forumId?: Guid;
  forums?: Forum[];
  getAllForums?: boolean;
  loadedForum?: Forum;
  onLeaveConversation?: () => void;
};

export default function ChatDialog(props: Readonly<ChatDialogProps>) {
  const { forumId, forums, getAllForums, loadedForum, onLeaveConversation } = props;
  const fillAvailableSpace = props.fillAvailableSpace ?? false;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allForums, setAllForums] = useState<Forum[]>([]);
  const [selectedForum, setSelectedForum] = useState<Forum | undefined>();
  const [isCreatingForum, setIsCreatingForum] = useState(false);
  const [isForumUnavailable, setIsForumUnavailable] = useState(false);
  const notifiedDeletedThreadIds = useRef(new Set<string>());
  const [getForums] = useLazyGetForumsQuery();
  const currentSelectedForum = selectedForum ?? loadedForum;

  const showDeletedItemsNotice = useCallback((requestedForumId: Guid) => {
    if (notifiedDeletedThreadIds.current.has(requestedForumId.value)) return;
    notifiedDeletedThreadIds.current.add(requestedForumId.value);
    enqueueSnackbar(deletedItemsMessage, {
      key: `thread-contains-deleted-items-${requestedForumId.value}`,
      preventDuplicate: true,
      variant: "info",
    });
  }, []);

  const loadChatDialogForCurrentForum = useCallback(
    (abortController: AbortController) =>
      loadChatDialog(
        abortController,
        forumId,
        getForums,
        setIsLoading,
        setSelectedForum,
        setAllForums,
        setIsForumUnavailable,
        showDeletedItemsNotice,
      ),
    [forumId, getForums, showDeletedItemsNotice],
  );

  const selectCreatedForum = useCallback((forum: Forum) => {
    setAllForums((forums) => [forum, ...forums.filter((existingForum) => !existingForum.isEqualTo(forum))]);
    setSelectedForum(forum);
    setIsCreatingForum(false);
    enqueueSnackbar(`Conversation ${forum.name} created`, { variant: "success" });
  }, []);

  useEffect(() => {
    if (forums) return;
    if (loadedForum) return;
    if (!forumId && !getAllForums) return;

    let abortController = new AbortController();
    queueMicrotask(() => {
      void loadChatDialogForCurrentForum(abortController);
    });

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [forumId, forums, getAllForums, loadChatDialogForCurrentForum, loadedForum]);

  return (
    <>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!isLoading && (
        <ForumContainer fillAvailableSpace={fillAvailableSpace}>
          {isForumUnavailable && (
            <UnavailableContainer>
              <Typography color="text.secondary">{forumUnavailableMessage}</Typography>
            </UnavailableContainer>
          )}
          {!isForumUnavailable && !forums && !getAllForums && !currentSelectedForum && (
            <SelectionContainer>
              {!isCreatingForum && (
                <>
                  <ForumSelection onForumSelected={setSelectedForum} />
                  <SelectionActions>
                    <Button
                      color="primary"
                      startIcon={<ForumIcon />}
                      variant="outlined"
                      onClick={() => setIsCreatingForum(true)}
                    >
                      Create Conversation
                    </Button>
                  </SelectionActions>
                </>
              )}
              {isCreatingForum && (
                <CreateForumDialog
                  onCancel={() => setIsCreatingForum(false)}
                  onForumCreated={selectCreatedForum}
                />
              )}
            </SelectionContainer>
          )}
          {!isForumUnavailable && !forums && getAllForums && (
            <ChatContainer fillAvailableSpace={fillAvailableSpace}>
              <ForumChatWindow
                fillAvailableSpace={fillAvailableSpace}
                forums={allForums}
                onLeaveConversation={onLeaveConversation}
              />
            </ChatContainer>
          )}
          {!isForumUnavailable && currentSelectedForum && (
            <ChatContainer fillAvailableSpace={fillAvailableSpace}>
              <ForumChatWindow
                fillAvailableSpace={fillAvailableSpace}
                forums={[currentSelectedForum]}
                onLeaveConversation={onLeaveConversation}
              />
            </ChatContainer>
          )}
          {!isForumUnavailable && forums && (
            <ChatContainer fillAvailableSpace={fillAvailableSpace}>
              <ForumChatWindow
                fillAvailableSpace={fillAvailableSpace}
                forums={forums}
                onLeaveConversation={onLeaveConversation}
              />
            </ChatContainer>
          )}
        </ForumContainer>
      )}
    </>
  );
}
